import { fetchJson, resolveMediaUrl } from "./api.js";

const PUBLIC_CATEGORY_SLUGS = [
  "gardens",
  "lawn-care",
  "tree-trimming-removal",
  "planting",
  "irrigation",
  "landscaping",
  "seasonal-cleaning",
];

export { PUBLIC_CATEGORY_SLUGS };

/**
 * @param {string} filterId - "all" or a server category slug
 * @returns {string|undefined} API category slug or undefined for all
 */
export function galleryCategoryFilterToApiSlug(filterId) {
  if (!filterId || filterId === "all") return undefined;
  const s = String(filterId).toLowerCase();
  return PUBLIC_CATEGORY_SLUGS.includes(s) ? s : undefined;
}

function normalizeItem(raw) {
  const fullSrc = resolveMediaUrl(raw.urlPath);
  return {
    id: String(raw.id),
    category: raw.category,
    location: raw.location || "",
    src: fullSrc,
    fullSrc,
    alt: String(raw.alt || raw.title || "").trim() || "Gallery photo",
    description: String(raw.description || "").trim(),
  };
}

/**
 * All albums from the API (GalleryLocation), for public filters.
 */
export async function fetchPublicGalleryAlbums() {
  const data = await fetchJson("/api/uploads/public/gallery/locations");
  return Array.isArray(data?.locations) ? data.locations : [];
}

/**
 * Fetches published gallery images for the public site.
 * @param {string} categoryFilterId - "all" or a category slug (e.g. lawn-care)
 * @param {string} albumSlug - "all" or location slug
 */
export async function fetchPublicGalleryForFilter(categoryFilterId, albumSlug) {
  const params = new URLSearchParams();
  const cat = galleryCategoryFilterToApiSlug(categoryFilterId);
  if (cat) params.set("category", cat);
  if (albumSlug && albumSlug !== "all") {
    params.set("location", String(albumSlug).trim().toLowerCase());
  }
  const q = params.toString() ? `?${params}` : "";
  const data = await fetchJson(`/api/uploads/public/gallery${q}`);
  const list = Array.isArray(data.images) ? data.images : [];
  return list.map(normalizeItem);
}

const CAROUSEL_ROW_SIZE = 10;
const CAROUSEL_FETCH = 30;

function carouselVisualShape(id) {
  let h = 0;
  for (let i = 0; i < id.length; i += 1) {
    h = (h * 31 + id.charCodeAt(i)) >>> 0;
  }
  const formats = ["wide", "normal", "square"];
  const f = formats[h % 3];
  const widths = { wide: 450, normal: 350, square: 280 };
  return { format: f, width: widths[f] };
}

function mapApiToCarouselItem(raw) {
  const id = String(raw.id);
  const src = raw.thumbUrlPath
    ? resolveMediaUrl(raw.thumbUrlPath)
    : resolveMediaUrl(raw.urlPath);
  const { format, width } = carouselVisualShape(id);
  return {
    id,
    src,
    width,
    format,
    alt: String(raw.alt || raw.title || "").trim() || "Gallery photo",
    description: String(raw.description || "").trim(),
  };
}

/**
 * Home page: three rows × 10 images = 30 newest published uploads (by createdAt).
 */
export async function fetchHomeCarouselConfig() {
  const q = new URLSearchParams({
    sort: "newest",
    limit: String(CAROUSEL_FETCH),
    offset: "0",
  });
  const data = await fetchJson(`/api/uploads/public/gallery?${q}`);
  const list = Array.isArray(data.images) ? data.images : [];
  const items = list.map(mapApiToCarouselItem);
  return [
    {
      images: items.slice(0, CAROUSEL_ROW_SIZE),
      duration: 35,
      reverse: false,
      rowId: "home-carousel-newest",
    },
    {
      images: items.slice(CAROUSEL_ROW_SIZE, CAROUSEL_ROW_SIZE * 2),
      duration: 45,
      reverse: true,
      rowId: "home-carousel-mid",
    },
    {
      images: items.slice(CAROUSEL_ROW_SIZE * 2, CAROUSEL_FETCH),
      duration: 40,
      reverse: false,
      rowId: "home-carousel-older",
    },
  ];
}
